import { encodeAbiParameters, parseAbiParameters } from 'viem'
import { aaveTokens } from '@1delta/data-sdk'
import { Call } from '../../../utils/multicall'
import { isFailedCall } from '../../../utils/multicall'
import {
  decodeLensRows,
  getPositionLensFor,
  LENS_FAMILY,
  PositionLensAbi,
} from '../position-lens'
import {
  getAaveTypePoolDataProviderAddress,
  getAaveTypePoolAddress,
  getAaveAssets,
} from '../../../assets'

const ZERO = '0x0000000000000000000000000000000000000000'

/**
 * A `uint8` flag byte + `uint16 eMode` sit ahead of the row count.
 * @see `PositionLens.aave`
 */
const AAVE_LENS_HEADER_BYTES = 3
/** Header bit0 — the pool answered `getUserEMode`. */
const HEADER_EMODE_OK = 1
/**
 * Header bit1 — the pool answered `getUserConfiguration`, so the collateral bits
 * were unioned into the probe.
 *
 * ZERO ON THE DEPLOYED v3, which does not read the bitmap at all, and 1 on the
 * fixed contract once it rolls out. Read as a BITMASK for exactly that reason:
 * the byte is `1` on v3 and `3` on v4 when both reads succeed, so an equality
 * check against `1` passes today and silently declines every Aave set the moment
 * the new contract lands — a whole family of positions vanishing on deploy day,
 * with the deploy looking clean.
 */
const HEADER_CONFIG_OK = 2
/** Words per row — see the layout note on `PositionLens.aave`. */
const AAVE_LENS_WORDS = 7

/**
 * The four addresses one reserve needs in the lens payload, or `undefined` when
 * the metadata does not carry them.
 */
const reserveTuple = (
  tokens: any,
  asset: string,
): [string, string, string, string] | undefined => {
  const t = tokens?.[asset.toLowerCase()]
  // The aToken and the variable debt token are the PROBE. Without them the lens
  // would probe `address(0)`, read zero, and skip the reserve — reporting a real
  // position as nothing. A stable debt token is genuinely absent on most V3
  // reserves and the contract skips a zero address there by design, so only that
  // one may default.
  if (!t?.aToken || !t?.vToken) return undefined
  return [asset, t.aToken, t.vToken, t.sToken ?? ZERO]
}

/**
 * True when this lender's reads on this chain go through `PositionLens`.
 *
 * KEYED ON THE LENDER, NOT JUST THE CHAIN, and that is the whole point. The lens
 * payload needs the aToken and variable-debt-token address of every reserve plus
 * the pool and data provider, and a fork can be perfectly well configured for the
 * walk — which only ever passes the ASSET to `getUserReserveData` — while carrying
 * none of them. Deciding on the chain alone throws on the missing pool address for
 * some forks and, far worse, silently reports "no positions" for any reserve whose
 * token addresses are absent, because the probe reads zero from `address(0)`.
 *
 * So this returns false unless EVERY input resolves, and the caller takes the
 * per-reserve walk. Fail closed toward the slower path, never toward the empty one.
 *
 * The builder and the converter both call this; they never each decide.
 */
export const usesAaveLens = (chainId: string, lender: string): boolean => {
  if (getPositionLensFor(chainId, LENS_FAMILY.AAVE) === undefined) return false
  if (
    !getAaveTypePoolDataProviderAddress(chainId, lender) ||
    !getAaveTypePoolAddress(chainId, lender)
  ) {
    return false
  }
  const tokens = (aaveTokens() as any)?.[lender]?.[chainId]
  if (!tokens) return false
  const assets = getAaveAssets(chainId, lender)
  if (assets.length === 0) return false
  return assets.every((a) => reserveTuple(tokens, a) !== undefined)
}

/**
 * ONE call instead of one `getUserReserveData` per reserve plus the e-mode read.
 *
 * The saving is proportional to the reserve count, which is what makes Aave the
 * broadest family: 48 reserves on Kaia, 42 on Hemi, 33 on Gnosis — and an account
 * is typically in one or two of them, or none.
 *
 * The lens probes the SCALED balances (one storage slot on each of the aToken,
 * the variable debt token and — where it exists — the stable debt token) and pays
 * for the full `getUserReserveData`, which walks the reserve struct and does index
 * math, only on reserves that answer non-zero.
 */
const buildAaveLensCall = (
  chainId: string,
  lender: string,
  account: string,
  assetsToQuery: string[],
  withEMode: boolean,
): Call[] => {
  const lens = getPositionLensFor(chainId, LENS_FAMILY.AAVE)
  if (!lens) return []

  const tokens = (aaveTokens() as any)?.[lender]?.[chainId] ?? {}
  const reserves: [string, string, string, string][] = []
  for (const asset of assetsToQuery) {
    const t = reserveTuple(tokens, asset)
    // Unreachable via `usesAaveLens`, which already required every reserve to
    // resolve. Kept as a hard stop so a future caller that skips the predicate
    // gets an empty call list — and therefore the walk — rather than a payload
    // with holes in it.
    if (!t) return []
    reserves.push(t)
  }

  const payload = encodeAbiParameters(
    parseAbiParameters('(address,address,address,address)[], address, address'),
    [
      reserves as any,
      getAaveTypePoolDataProviderAddress(chainId, lender) as `0x${string}`,
      // A zero pool disables the e-mode read, which is exactly what YLDR wants:
      // it has no `getUserEMode` and the walk omits that call for it too.
      (withEMode
        ? getAaveTypePoolAddress(chainId, lender)
        : ZERO) as `0x${string}`,
    ],
  )
  return [
    {
      address: lens,
      name: 'getUserData',
      params: [[{ family: LENS_FAMILY.AAVE, payload }], account],
      abi: PositionLensAbi as any,
    },
  ]
}

export { buildAaveLensCall }

/**
 * Rebuild the per-reserve `data` array the roster walk would have produced.
 *
 * DELIBERATELY a reconstruction rather than a second converter. Every field the
 * Aave converters read is indexed off `AaveTypeGetUserReserveIndexes`, so handing
 * them the same shape means the two read paths cannot drift in interpretation —
 * only in transport. A reserve the lens did not emit is filled with ZEROS, which
 * is precisely what the walk returns for an account with nothing in it.
 *
 * Returns `undefined` when the blob is unusable, which the caller must treat as an
 * incomplete read (decline the set) and never as an empty position.
 *
 * THE `liquidityRate` SLOT IS NOT POPULATED, and that is not an oversight.
 * `getUserReserveData` returns `liquidityRate` at index 6 and `stableRateLastUpdated`
 * at index 7 — confirmed against a live mainnet return — but the deployed v3 lens
 * skips index 6 and emits index 7 in its place. No converter reads either field,
 * so the deployed contract is correct for every value that reaches a user; filling
 * the slot from the lens would silently publish a TIMESTAMP as a rate the moment
 * someone starts reading it. Fixed in the contract source for the next version.
 */
export const rebuildAaveReserveData = (
  blobs: unknown,
  assetCount: number,
  withEMode: boolean,
): any[] | undefined => {
  if (isFailedCall(blobs) || !Array.isArray(blobs) || blobs.length !== 1) {
    return undefined
  }
  let decoded
  try {
    decoded = decodeLensRows(
      blobs[0] as string,
      AAVE_LENS_WORDS,
      AAVE_LENS_HEADER_BYTES,
      1,
    )
  } catch {
    return undefined
  }
  const { header, rows } = decoded

  // The e-mode read is a SET-LEVEL fact: it selects the collateral factors every
  // reserve below is scored against. `header[0] == 0` means the pool did not
  // answer, and defaulting to 0 would report an account that IS in an e-mode as
  // near-liquidation. Decline the set, exactly as the walk does on a failed call.
  const eModeOk = (header[0] & HEADER_EMODE_OK) !== 0
  if (withEMode && !eModeOk) return undefined
  const eMode = (header[1] << 8) | header[2]
  // Not consulted yet: on v3 the collateral flags come only from the rows the
  // balance probe emitted, and there is no bitmap to cross-check them against.
  // Kept named so the rollout has an explicit place to branch rather than a
  // magic number to rediscover.
  void ((header[0] & HEADER_CONFIG_OK) !== 0)

  const out: any[] = Array.from({ length: assetCount }, () => [
    0n, 0n, 0n, 0n, 0n, 0n, 0n, 0n, false,
  ])
  for (const r of rows) {
    if (r.index >= assetCount) return undefined
    const [
      aTokenBalance,
      stableDebt,
      variableDebt,
      principalStable,
      scaledVariable,
      stableRate,
    ] = r.words
    out[r.index] = [
      aTokenBalance,
      stableDebt,
      variableDebt,
      principalStable,
      scaledVariable,
      stableRate,
      0n, // liquidityRate — see the note above
      0n, // stableRateLastUpdated
      (r.flags & 1) === 1,
    ]
  }
  if (withEMode) out.push(eMode)
  return out
}
